import type { ReactNode } from 'react'
import { useOptionalT } from '../i18n/LocaleProvider'
import { SmartLink } from './SmartLink'
import type { LinkTarget } from './types'

/**
 * The adventure register's section shapes — the dark-first counterpart to `Band`,
 * `SectionHeading` and `HighlightGrid`.
 *
 * **Why they share one file.** Nine of these are twenty lines each and none of them means
 * anything without the others: an `AdvSection` with no `AdvHeading` in it is an empty box,
 * and a `DisplayTitle` outside one has no ground to sit on. Nine files of twenty lines
 * would hide that they are one vocabulary. The product-page shapes, which are larger and
 * are used on one route, sit in their own file.
 *
 * All the styling is in `styles/sections/_adventure.scss`, and the class names are the
 * contract between the two halves — ground rule 2. Read that sheet's header before
 * changing anything here; in particular, the glow placements are what separate adjacent
 * sections on a page that has no alternating grounds to do it with.
 */

// ── the band ────────────────────────────────────────────────────────────────────────────

/** Where the section's glow sits. Adjacent sections must not repeat one. */
export type AdvGlow = 'tr' | 'tl' | 'bl' | 'br' | 'band' | 'signal' | 'none'

export interface AdvSectionProps {
  children: ReactNode
  /**
   * The glow, which is this register's substitute for `Band`'s alternating grounds. On a
   * continuous near-black page there are no grounds left to alternate, so depth comes
   * from light — and two adjacent sections lit identically read as one column, which is
   * the whole problem it exists to solve.
   */
  glow?: AdvGlow
  /** A hairline above, for two sections that must sit flush and still separate. */
  edged?: boolean
  /** Half the vertical air — for a strip rather than a section. */
  tight?: boolean
  /** No top padding, for a section that must butt against the one above it. */
  flush?: boolean
  /**
   * No bottom padding — for a section whose content is the full-bleed thing itself. Two
   * stacked paddings under a row of tiles read as a missing section.
   */
  flushBottom?: boolean
  className?: string
  /** Extra classes on the inner container, e.g. `wow animate__fadeInUp`. */
  containerClassName?: string
  /** Full-bleed content: no container at all. The terrain tiles need this. */
  bleed?: boolean
  id?: string
}

export function AdvSection({
  children,
  glow = 'none',
  edged = false,
  tight = false,
  flush = false,
  flushBottom = false,
  className,
  containerClassName,
  bleed = false,
  id,
}: AdvSectionProps) {
  const classes = [
    'ul-adv-section',
    glow !== 'none' ? `ul-glow--${glow}` : '',
    edged ? 'ul-adv-section--edged' : '',
    tight ? 'ul-adv-section--tight' : '',
    flush ? 'ul-adv-section--flush' : '',
    flushBottom ? 'ul-adv-section--flush-b' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <section className={classes} {...(id ? { id } : {})}>
      {bleed ? (
        children
      ) : (
        <div className={containerClassName ? `ul-adv-container ${containerClassName}` : 'ul-adv-container'}>
          {children}
        </div>
      )}
    </section>
  )
}

// ── the eyebrow ─────────────────────────────────────────────────────────────────────────

export interface EyebrowProps {
  children: ReactNode
  centered?: boolean
  /** The bordered chip, for a hero where the label sits over a photograph. */
  pill?: boolean
  className?: string
}

/**
 * The mono label that announces a section: a hairline, then letterspaced uppercase in the
 * interface accent. It is the only place that accent appears in body-sized type —
 * everywhere else it touches is a control.
 */
export function Eyebrow({ children, centered = false, pill = false, className }: EyebrowProps) {
  const classes = [
    'ul-eyebrow',
    centered ? 'ul-eyebrow--centered' : '',
    pill ? 'ul-eyebrow--pill' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  return <span className={classes}>{children}</span>
}

// ── the display line ────────────────────────────────────────────────────────────────────

export interface DisplayTitleProps {
  /** The opening words, in the page's own ink. */
  children: ReactNode
  /**
   * The closing words, in the display accent. Two-tone is the device that makes a wall of
   * uppercase read as language rather than as signage — a title with no `accent` is
   * legal, and flatter.
   */
  accent?: ReactNode
  size?: 'sm' | 'md' | 'lg'
  /** `h1` on a hero, `h2` on a section. Defaults to `h2`. */
  as?: 'h1' | 'h2' | 'h3'
  className?: string
}

export function DisplayTitle({ children, accent, size = 'lg', as = 'h2', className }: DisplayTitleProps) {
  const Tag = as
  const classes = ['ul-display-title', size !== 'lg' ? `ul-display-title--${size}` : '', className ?? '']
    .filter(Boolean)
    .join(' ')

  return (
    <Tag className={classes}>
      {children}
      {/* The accent is a span inside the same heading, not a sibling block: two blocks
          would force a line break at the join on every viewport, and the whole point is
          that the line wraps as one paragraph. */}
      {accent !== undefined && (
        <>
          {' '}
          <span className="accent">{accent}</span>
        </>
      )}
    </Tag>
  )
}

// ── the heading block ───────────────────────────────────────────────────────────────────

export interface AdvHeadingProps {
  eyebrow: ReactNode
  title: ReactNode
  /** The display accent's half of the title. */
  titleAccent?: ReactNode
  lead?: ReactNode
  centered?: boolean
  size?: DisplayTitleProps['size']
  as?: DisplayTitleProps['as']
  /** A control beside the heading — "All tours →" against the section title. */
  aside?: ReactNode
  className?: string
}

export function AdvHeading({
  eyebrow,
  title,
  titleAccent,
  lead,
  centered = false,
  size = 'md',
  as = 'h2',
  aside,
  className,
}: AdvHeadingProps) {
  const classes = [
    'ul-adv-heading',
    centered ? 'ul-adv-heading--centered' : '',
    aside ? 'ul-adv-heading--split' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ')

  const head = (
    <div>
      <Eyebrow centered={centered}>{eyebrow}</Eyebrow>
      <DisplayTitle accent={titleAccent} size={size} as={as}>
        {title}
      </DisplayTitle>
      {lead && <p className="ul-adv-lead">{lead}</p>}
    </div>
  )

  if (!aside) return <div className={classes}>{head}</div>

  return (
    <div className={classes}>
      {head}
      <div className="right">{aside}</div>
    </div>
  )
}

// ── the stat row ────────────────────────────────────────────────────────────────────────

export interface AdvStat {
  /** The figure. Keep it short — this is set in the display face at heading size. */
  figure: string
  /** The unit or the suffix, one step down and in the display accent: `+`, `nit`, `km`. */
  unit?: string
  label: string
}

export function StatRow({ items, className }: { items: AdvStat[]; className?: string }) {
  return (
    <div className={className ? `ul-stat-row ${className}` : 'ul-stat-row'}>
      {items.map((s) => (
        <div className="ul-stat-row-item" key={s.label}>
          <span className="ul-stat-row-figure">
            {s.figure}
            {s.unit && <span className="unit">{s.unit}</span>}
          </span>
          <span className="ul-stat-row-label">{s.label}</span>
        </div>
      ))}
    </div>
  )
}

// ── the hero ────────────────────────────────────────────────────────────────────────────

export interface AdventureHeroProps {
  image: string
  imageAlt: string
  /** The chip above the display line — an announcement, not a section marker. */
  pill?: ReactNode
  title: ReactNode
  titleAccent?: ReactNode
  /** The mono line under the title: where this place is, as a fact. */
  coords?: string
  lead?: ReactNode
  children?: ReactNode
  stats?: AdvStat[]
  /** The vertical rail in the corner. Decoration — hidden from assistive tech. */
  scrollLabel?: string
}

/**
 * A full-bleed photograph with the display line over it.
 *
 * This is the shape `BannerSlider` occupies on the pale apps, and it is a separate
 * component rather than a variant of it because almost nothing is shared: no Swiper, no
 * thumbnails, no slide array, and a scrim built for copy on one side rather than centred
 * over the frame. `BannerSlider` stays exactly as it is for the other three.
 */
export function AdventureHero({
  image,
  imageAlt,
  pill,
  title,
  titleAccent,
  coords,
  lead,
  children,
  stats,
  scrollLabel: scrollLabelProp,
}: AdventureHeroProps) {
  const t = useOptionalT()
  const scrollLabel = scrollLabelProp ?? t('ul.adventure.scroll')
  return (
    <section className="ul-adv-hero">
      <div className="ul-adv-hero-media">
        <img src={image} alt={imageAlt} />
      </div>

      <div className="ul-adv-container">
        <div className="ul-adv-hero-content wow animate__fadeInUp">
          {pill && <Eyebrow pill>{pill}</Eyebrow>}
          <DisplayTitle accent={titleAccent} as="h1">
            {title}
          </DisplayTitle>
          {coords && <span className="ul-adv-hero-coords">{coords}</span>}
          {lead && <p className="ul-adv-lead">{lead}</p>}
          {children && <div className="ul-adv-hero-btns">{children}</div>}
          {stats && stats.length > 0 && <StatRow items={stats} />}
        </div>
      </div>

      {/* No reading of its own: it names an affordance the viewport already has. */}
      <span className="ul-adv-hero-rail" aria-hidden="true">
        {scrollLabel}
      </span>
    </section>
  )
}

// ── the terrain tiles ───────────────────────────────────────────────────────────────────

export interface TerrainTileData extends LinkTarget {
  /** The mono label over the title — `TERRAIN / 4X4`. */
  kicker: string
  title: string
  linkLabel: string
  image: string
  imageAlt: string
}

/**
 * Full-bleed image tiles, edge to edge with no gutter.
 *
 * The whole tile is the link, so the text inside it is not a second one — three anchors
 * per tile would give a keyboard three stops for one destination.
 */
export function TerrainTiles({ items }: { items: TerrainTileData[] }) {
  return (
    <div className="ul-terrain-grid">
      {items.map((t) => (
        <SmartLink key={t.title} to={t.to} href={t.href} className="ul-terrain-tile">
          <img src={t.image} alt={t.imageAlt} />
          <span className="ul-terrain-tile-body">
            <span className="ul-eyebrow">{t.kicker}</span>
            {/* A heading element inside a link is legal and is what gives the tile a
                place in the document outline — the alternative is three unlabelled
                landmarks. */}
            <span className="ul-terrain-tile-title d-block">{t.title}</span>
            <span className="ul-terrain-tile-link">
              {t.linkLabel} <i className="flaticon-fast-forward-double-right-arrows-symbol" />
            </span>
          </span>
        </SmartLink>
      ))}
    </div>
  )
}

// ── the bordered card ───────────────────────────────────────────────────────────────────

export interface IconCardData {
  /** Flaticon suffix, rendered as a line glyph in the interface accent. */
  icon?: string
  title: string
  descr: string
  /** Turns the card into the image-topped variant. */
  image?: string
  imageAlt?: string
}

export function IconCard({ item }: { item: IconCardData }) {
  if (item.image) {
    return (
      <div className="ul-icon-card ul-icon-card--media">
        <div className="ul-icon-card-media">
          <img src={item.image} alt={item.imageAlt ?? ''} />
        </div>
        <div className="ul-icon-card-body">
          <h3 className="ul-icon-card-title">{item.title}</h3>
          <p className="ul-icon-card-txt">{item.descr}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="ul-icon-card">
      {item.icon && (
        <span className="ul-icon-card-icon">
          <i className={`flaticon-${item.icon}`} />
        </span>
      )}
      <h3 className="ul-icon-card-title">{item.title}</h3>
      <p className="ul-icon-card-txt">{item.descr}</p>
    </div>
  )
}

/** A responsive grid of `IconCard`s. Three up by default, two below `md`, one below `sm`. */
export function IconCardGrid({ items, cols = 4 }: { items: IconCardData[]; cols?: 2 | 3 | 4 }) {
  const rowCols = cols === 2 ? 'row-cols-md-2' : cols === 3 ? 'row-cols-lg-3 row-cols-md-2' : 'row-cols-lg-4 row-cols-md-2'
  return (
    <div className={`row ${rowCols} row-cols-1 ul-bs-row`}>
      {items.map((item) => (
        <div className="col" key={item.title}>
          <IconCard item={item} />
        </div>
      ))}
    </div>
  )
}

// ── the spec grid ───────────────────────────────────────────────────────────────────────

export interface SpecCell {
  label: string
  /** The figure. Omitted or empty when `pending` — see below. */
  value?: string
  /** The unit, one step down and in the display accent. */
  unit?: string
  /**
   * True while nobody has given us this number.
   *
   * It renders "to be confirmed" in the body face rather than the display face, so it
   * reads as prose in a numeric slot and cannot be taken for a figure at a glance. It is
   * deliberately still legible: on a product page an invented number is a claim somebody
   * may act on, and a spec sheet that hides its gaps is worse than one that admits them.
   */
  pending?: boolean
}

export interface SpecGridProps {
  items: SpecCell[]
  /** Two columns instead of three — for a narrow column, or a short list. */
  cols?: 2 | 3
  /** What a pending cell says. */
  pendingLabel?: string
  className?: string
}

export function SpecGrid({ items, cols = 3, pendingLabel: pendingLabelProp, className }: SpecGridProps) {
  const t = useOptionalT()
  const pendingLabel = pendingLabelProp ?? t('ul.adventure.pending')
  const classes = ['ul-spec-grid', cols === 2 ? 'ul-spec-grid--2' : '', className ?? ''].filter(Boolean).join(' ')

  return (
    <div className={classes}>
      {items.map((s) => (
        <div className="ul-spec-grid-cell" key={s.label}>
          {s.pending || !s.value ? (
            <span className="ul-spec-grid-value ul-spec-grid-value--pending">{pendingLabel}</span>
          ) : (
            <span className="ul-spec-grid-value">
              {s.value}
              {s.unit && <span className="unit">{s.unit}</span>}
            </span>
          )}
          <span className="ul-spec-grid-label">{s.label}</span>
        </div>
      ))}
    </div>
  )
}

// ── the spec sheet ──────────────────────────────────────────────────────────────────────

export interface SheetRow {
  label: string
  /** One or more values, stacked. Empty while `pending`. */
  values: string[]
  pending?: boolean
}

/**
 * The prose counterpart to `SpecGrid`: a mono label, then the value in the body face.
 *
 * **Pick between the two by the value, not by the position.** `SpecGrid` sets values in the
 * display face at heading size, which is right for a figure and unreadable for a sentence —
 * six sentences through it produced a wall of 26px uppercase on the garage's first draft.
 * If the value would look right on a dashboard it is a grid; if it would look right in a
 * manual it is a sheet.
 */
export function SpecSheet({
  rows,
  pendingLabel: pendingLabelProp,
  className,
}: {
  rows: SheetRow[]
  pendingLabel?: string
  className?: string
}) {
  const t = useOptionalT()
  const pendingLabel = pendingLabelProp ?? t('ul.adventure.pending')
  return (
    <div className={className ? `ul-spec-sheet ${className}` : 'ul-spec-sheet'}>
      {rows.map((r) => (
        <div className="ul-spec-sheet-row" key={r.label}>
          <span className="ul-spec-sheet-label">{r.label}</span>
          <div className="ul-spec-sheet-values">
            {r.pending || r.values.length === 0 ? (
              <span className="ul-spec-sheet-pending">{pendingLabel}</span>
            ) : (
              r.values.map((v) => <span key={v}>{v}</span>)
            )}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── the spotlight ───────────────────────────────────────────────────────────────────────

export interface SpotlightProps {
  image: string
  imageAlt: string
  eyebrow: ReactNode
  title: ReactNode
  titleAccent?: ReactNode
  /** The accent-coloured line under the name. */
  claim?: ReactNode
  lead?: ReactNode
  points?: string[]
  specs?: SpecCell[]
  specCols?: 2 | 3
  /** A spec SHEET instead of a grid — for values that are sentences rather than figures. */
  sheet?: SheetRow[]
  /** The buttons. */
  children?: ReactNode
  /** Image on the right instead of the left. */
  reverse?: boolean
}

/** The two-column feature: photograph one side, the argument and the numbers the other. */
export function Spotlight({
  image,
  imageAlt,
  eyebrow,
  title,
  titleAccent,
  claim,
  lead,
  points,
  specs,
  specCols = 3,
  sheet,
  children,
  reverse = false,
}: SpotlightProps) {
  return (
    <div className={reverse ? 'ul-spotlight ul-spotlight--reverse' : 'ul-spotlight'}>
      <div className="ul-spotlight-media">
        <img src={image} alt={imageAlt} />
      </div>

      <div className="ul-spotlight-body">
        <Eyebrow>{eyebrow}</Eyebrow>
        <DisplayTitle accent={titleAccent} size="sm">
          {title}
        </DisplayTitle>
        {claim && <p className="ul-spotlight-claim">{claim}</p>}
        {lead && <p className="ul-adv-lead">{lead}</p>}
        {points && points.length > 0 && (
          <ul className="ul-spotlight-points">
            {points.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        {specs && specs.length > 0 && <SpecGrid items={specs} cols={specCols} />}
        {sheet && sheet.length > 0 && <SpecSheet rows={sheet} />}
        {children && <div className="ul-spotlight-btns">{children}</div>}
      </div>
    </div>
  )
}

// ── the wide card ───────────────────────────────────────────────────────────────────────

export interface WideCardData extends LinkTarget {
  meta: string
  title: string
  descr: string
  image: string
  imageAlt: string
  linkLabel: string
}

export function WideCard({ item }: { item: WideCardData }) {
  return (
    <div className="ul-wide-card">
      <div className="ul-wide-card-media">
        <img src={item.image} alt={item.imageAlt} />
      </div>
      <div className="ul-wide-card-body">
        <span className="ul-wide-card-meta">{item.meta}</span>
        <h3 className="ul-wide-card-title">{item.title}</h3>
        <p className="ul-wide-card-txt">{item.descr}</p>
        <SmartLink to={item.to} href={item.href} className="ul-wide-card-link">
          {item.linkLabel} <i className="flaticon-fast-forward-double-right-arrows-symbol" />
        </SmartLink>
      </div>
    </div>
  )
}

/** The row of mono names under a set of cards — the rest of a range, unexpanded. */
export function MonoRow({ items }: { items: string[] }) {
  return (
    <ul className="ul-mono-row">
      {items.map((i) => (
        <li key={i}>{i}</li>
      ))}
    </ul>
  )
}

// ── the panel ───────────────────────────────────────────────────────────────────────────

export interface AdvPanelProps {
  /** The mono URL line at the top — a destination, stated as an address. */
  url?: string
  children: ReactNode
  links?: (LinkTarget & { label: string })[]
}

export function AdvPanel({ url, children, links }: AdvPanelProps) {
  return (
    <div className="ul-adv-panel">
      {url && <span className="ul-adv-panel-url">{url}</span>}
      {children}
      {links && links.length > 0 && (
        <div className="ul-adv-panel-links">
          {links.map((l) => (
            <SmartLink key={l.label} to={l.to} href={l.href}>
              {l.label}
            </SmartLink>
          ))}
        </div>
      )}
    </div>
  )
}

// ── the filterable listing ──────────────────────────────────────────────────────────────

export interface AdvListingProps {
  /** The left rail — normally a `DirectoryFilters`. */
  rail: ReactNode
  /** The results. */
  children: ReactNode
}

/**
 * A left rail and the results beside it.
 *
 * Layout only: it owns no state and does no filtering, so the same shell serves a listing
 * of tours, of services and of programmes. The consumer owns the query and the selections
 * and hands in whichever rail it wants — which in practice is `DirectoryFilters`, reused
 * from Partners' directory rather than reimplemented, because it was already fully
 * controlled and content-agnostic and only wanted a dark skin.
 */
export function AdvListing({ rail, children }: AdvListingProps) {
  return (
    <div className="ul-adv-listing">
      {rail}
      <div>{children}</div>
    </div>
  )
}

/**
 * What a listing shows when the filters match nothing.
 *
 * A listing with filters needs one: a combination that matches nothing is reachable in a
 * single click, and a page that answers it with blank space reads as broken rather than as
 * empty. So it says so, and offers the way back.
 */
export function AdvEmpty({
  title: titleProp,
  children,
  onClear,
  clearLabel: clearLabelProp,
}: {
  title?: string
  children?: ReactNode
  onClear?: () => void
  clearLabel?: string
}) {
  const t = useOptionalT()
  const title = titleProp ?? t('ul.adventure.noMatch')
  const clearLabel = clearLabelProp ?? t('ul.adventure.clearFilters')
  return (
    <div className="ul-adv-empty">
      <p className="ul-adv-empty-title">{title}</p>
      {children && <p className="ul-adv-empty-txt">{children}</p>}
      {onClear && (
        <button type="button" className="ul-btn ul-btn--ghost" onClick={onClear}>
          {clearLabel}
        </button>
      )}
    </div>
  )
}

// ── the quote marquee ───────────────────────────────────────────────────────────────────

export interface QuoteCardData {
  quote: string
  by: string
  /** The second attribution line — the garage's "testimony to be recorded". */
  note?: string
  /** Out of five. Omit where there is nothing to rate. */
  stars?: number
}

function QuoteCard({ item }: { item: QuoteCardData }) {
  return (
    <figure className="ul-quote-card">
      {item.stars !== undefined && (
        <div className="ul-quote-card-stars" aria-label={`${item.stars} out of 5`}>
          {'★'.repeat(item.stars)}
        </div>
      )}
      <blockquote className="ul-quote-card-txt">{item.quote}</blockquote>
      <figcaption className="ul-quote-card-by">
        <span>
          {item.by}
          {item.note && <span className="ul-quote-card-note">{item.note}</span>}
        </span>
      </figcaption>
    </figure>
  )
}

/**
 * Two rows of quote cards travelling in opposite directions.
 *
 * Each row's track is rendered twice and translated by exactly -50%, which is what makes
 * the loop seamless — at -50% the duplicate sits precisely where the original started. The
 * duplicate is `aria-hidden`, so a screen reader hears each quote once.
 *
 * Under `prefers-reduced-motion` the sheet stops the animation and turns each row into a
 * strip the reader scrolls themselves, and hides the duplicate track. That is a real
 * requirement, not a courtesy: a marquee that cannot be stopped is a defect.
 */
export function QuoteMarquee({ rows }: { rows: QuoteCardData[][] }) {
  return (
    <div className="ul-quote-marquee">
      {rows.map((row, i) => (
        <div
          className={i % 2 === 1 ? 'ul-quote-marquee-row ul-quote-marquee-row--reverse' : 'ul-quote-marquee-row'}
          key={i}
        >
          <div className="ul-quote-marquee-track">
            {row.map((q) => (
              <QuoteCard item={q} key={q.quote} />
            ))}
          </div>
          <div className="ul-quote-marquee-track" aria-hidden="true">
            {row.map((q) => (
              <QuoteCard item={q} key={`dup-${q.quote}`} />
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── the urgent band ─────────────────────────────────────────────────────────────────────

export interface AdvUrgentProps {
  eyebrow: ReactNode
  title: ReactNode
  titleAccent?: ReactNode
  note?: ReactNode
  /** The one control. A button, not a number — see below. */
  children: ReactNode
}

/**
 * The one loud thing on a page: the way to get help, at heading scale.
 *
 * The customer this is for is not reading the page — they are standing next to a vehicle
 * that will not start. So it takes the signal glow rather than the atmospheric one, and
 * there is exactly one of these per page.
 *
 * **It used to print the phone number itself, and that was worse.** The number in the data
 * is still the template's `123-456-7890` placeholder, so the loudest element on the page
 * was a fake number a stranded customer might actually dial. A button that starts a
 * request is honest at any stage of the build, and it stays right when the real number
 * arrives — at which point a `tel:` link beside it is an addition, not a replacement.
 */
export function AdvUrgent({ eyebrow, title, titleAccent, note, children }: AdvUrgentProps) {
  return (
    <div className="ul-adv-urgent">
      <div>
        <Eyebrow>{eyebrow}</Eyebrow>
        <DisplayTitle accent={titleAccent} size="sm">
          {title}
        </DisplayTitle>
        {note && <p className="ul-adv-lead">{note}</p>}
      </div>
      <div className="ul-adv-urgent-action">{children}</div>
    </div>
  )
}
